import React, { useEffect, useRef, useState } from 'react'
import { getOptimizedUrl } from '@disco/api/imgixClient'
import AspectRatio from 'components/ui/aspect-ratio'
import Icon from 'components/ui/icon'
import styles from './index.module.css'

type Props = {
  color: string | null | undefined
  overlay?: React.ReactNode
  status?: React.ReactNode
  icon?: React.ReactNode
  image: string | null | undefined
  imageParams?: Record<string, any>
}

/**
 * Album artwork component with optimized image loading and fallback icon
 * @param color - Background color for the artwork
 * @param overlay - Optional overlay content to display on top
 * @param status - Optional status indicator to show below the artwork
 * @param icon - Custom icon to display when no image is available
 * @param image - URL of the album artwork image
 * @param imageParams - Parameters for image optimization
 */
const AlbumArtwork = ({
  color,
  overlay,
  status,
  icon,
  image,
  imageParams,
}: Props): React.ReactElement<'div'> => {
  const albumArtworkRef = useRef(null)
  const [buildURL, setBuildURL] = useState(null)
  useEffect(() => {
    if (!image) {
      setBuildURL(null)
      return
    }

    const result = getOptimizedUrl({
      imageContainer: albumArtworkRef.current,
      image,
      params: imageParams,
    })
    // Fallback to original image if optimization fails
    setBuildURL(result || image)
  }, [image, imageParams, albumArtworkRef.current])
  return (
    <div className={styles.albumArtwork} ref={albumArtworkRef}>
      <AspectRatio fill={color} image={color ? undefined : buildURL} ratio={1}>
        {(!buildURL || Boolean(color)) &&
          (icon || <Icon type="soundwave" width={48} height={48} />)}
        {overlay}
      </AspectRatio>
      {status && <div className={styles.albumArtworkStatus}>{status}</div>}
    </div>
  )
}

export default AlbumArtwork
